import { Medal, Swords, BarChart3, MapPin, Newspaper } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useMessages } from '@/hooks/useMessages';
import { getInitials } from '@/utils/stringUtils';
import { ROUTES } from '@/constants/routes';
import { NAVIGATION_ITEMS } from '@/constants/navigation';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import BrandLogo from '@/components/BrandLogo';

type NavEntry = { icon: any; labelKey: string; label?: string; path: string; key: string };
const NAV_SECTIONS: { label: string; items: NavEntry[] }[] = [
  { label: 'Main', items: [...NAVIGATION_ITEMS].sort((a, b) => ['home','matches','groups','messages'].indexOf(a.key) - ['home','matches','groups','messages'].indexOf(b.key)) as NavEntry[] },
  { label: 'Competitions', items: [
    { icon: Medal, labelKey: '', label: 'Leagues', path: '/leagues', key: 'leagues' },
    { icon: Swords, labelKey: '', label: 'Tournaments', path: ROUTES.TOURNAMENTS, key: 'tournaments' },
    { icon: BarChart3, labelKey: '', label: 'Rankings', path: ROUTES.RANKINGS, key: 'rankings' },
  ] },
  { label: 'Explore', items: [
    { icon: MapPin, labelKey: '', label: 'Padel Courts', path: '/padel-courts-near-me', key: 'courts' },
    { icon: Newspaper, labelKey: '', label: 'Feed', path: ROUTES.FEED, key: 'feed' },
  ] },
];

export function AppSidebar() {
  const { open: isOpen } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { conversations } = useMessages();

  // Calculate total unread messages
  const totalUnreadMessages = conversations.reduce(
    (sum, conv) => sum + (conv.unread_count || 0),
    0
  );

  const isActive = (path: string) => location.pathname === path;

  if (!user || !profile) return null;

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border bg-[hsl(220,15%,15%)]">
      {/* Header with toggle */}
      <SidebarHeader className={cn(
        "flex flex-row items-center justify-between min-h-[64px]",
        isOpen ? "p-4" : "p-2 flex-col gap-2"
      )}>
        <BrandLogo showText={isOpen} />
        <SidebarTrigger className={cn("text-white hover:text-white/80", !isOpen && "ml-0")} />
      </SidebarHeader>

      {/* Navigation Content */}
      <SidebarContent>
        {NAV_SECTIONS.map((section) => (
        <SidebarGroup key={section.label}>
          {isOpen && (
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-white/50">
              {section.label}
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = item.path === '/' ? isActive(item.path) : location.pathname.startsWith(item.path);
                const showBadge = item.key === 'messages' && totalUnreadMessages > 0;

                return (
                  <SidebarMenuItem key={item.key}>
                    <SidebarMenuButton
                      onClick={() => navigate(item.path)}
                      isActive={active}
                      className={cn(
                        "relative min-h-[44px]",
                        active && "border-l-4 border-padel-green-light bg-sidebar-accent"
                      )}
                      tooltip={item.label ?? t(item.labelKey)}
                    >
                      <Icon className={cn(
                        "h-5 w-5 text-white/90",
                        active && "text-padel-green-light"
                      )} />
                      {isOpen && (
                        <span className={cn(
                          "text-white/90",
                          active && "font-semibold text-padel-green-light"
                        )}>{item.label ?? t(item.labelKey)}</span>
                      )}
                      {showBadge && isOpen && (
                        <Badge variant="destructive" className="ml-auto h-5 min-w-5 px-1 text-xs">
                          {totalUnreadMessages > 99 ? '99+' : totalUnreadMessages}
                        </Badge>
                      )}
                      {showBadge && !isOpen && (
                        <Badge variant="destructive" className="absolute -right-1 -top-1 h-4 min-w-4 px-1 text-xs border-2 border-sidebar">
                          {totalUnreadMessages > 9 ? '9+' : totalUnreadMessages}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        ))}
      </SidebarContent>

      {/* User Profile Footer */}
      <SidebarFooter className={cn(
        "border-t border-sidebar-border mt-auto",
        isOpen ? "p-2" : "p-1.5"
      )}>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => navigate(ROUTES.PROFILE)}
              className={cn("h-auto", isOpen ? "py-2" : "py-1.5 justify-center")}
              tooltip={isOpen ? undefined : profile.full_name || 'Profile'}
            >
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={profile.avatar_url || undefined} alt={profile.full_name || 'User'} />
                <AvatarFallback className="bg-padel-blue-light text-white text-xs">
                  {getInitials(profile.full_name)}
                </AvatarFallback>
              </Avatar>
              {isOpen && (
                <div className="flex flex-col items-start overflow-hidden">
                  <span className="text-sm font-medium truncate w-full text-white">
                    {profile.full_name}
                  </span>
                  <span className="text-xs text-white/60 truncate w-full">
                    @{profile.username}
                  </span>
                </div>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
